import _ from 'lodash';

export class PaymentService {
    constructor(localStorageService, restaurantInfoFactory, cartService, restaurantHoursService, ORDER_TYPES, LOCAL_STORAGE, $q, $log) {
        'ngInject';
        this.localStorageService = localStorageService;
        this.restaurantInfoFactory = restaurantInfoFactory;
        this.cartService = cartService;
        this.restaurantHoursService = restaurantHoursService;
        this.ORDER_TYPES = ORDER_TYPES;
        this.LOCAL_STORAGE = LOCAL_STORAGE;
        this.$q = $q;
        this.$log = $log;

        this.data = {
            cashPayment: null,
            cardPayment: null,
            selectedPayment: this._getDefaultPayment(),
            tempPayment: this._getDefaultPayment()
        };

        this.allMethods = {
            CASH_TAKEOUT: {
                id: 'CASH_TAKEOUT',
                displayName: 'Cash',
                method: 'CASH',
                body: 'Simply pay with cash when you pick up your food. Nothing to fill out here!'
            },
            CASH_DELIVERY: {
                id: 'CASH_DELIVERY',
                displayName: 'Cash',
                method: 'CASH',
                body: 'Simply pay with cash when your food arrives. Please remember to tip your driver.'
            },
            CREDIT: {
                id: 'CREDIT',
                displayName: 'Credit Card',
                method: 'CREDIT'
            },
            IN_STORE: {
                id: 'IN_STORE',
                displayName: 'In Store',
                method: 'CASH',
                body: 'Simply pay with cash or card when you pick up your food. Nothing to fill out here!'
            },
            CREDIT_IN_STORE: {
                id: 'CREDIT_IN_STORE',
                displayName: 'In Store',
                method: 'CASH',
                body: 'Simply pay with card when you pick up your food. Nothing to fill out here!'
            }
        };
    }

    _getDefaultPayment(method) {
        return {
            cardNumber: null,
            expirationDate: null,
            guid: null,
            isPrimary: null,
            method: method || this._getPaymentMethod(),
            postalCode: null,
            securityCode: null,
            saveCard: null,
            tip: 0.0,
            maskedPan: null
        };
    }

    setCashPayment(cashPayment = null) {
        Object.assign(this.data, { cashPayment });
    }

    setCardPayment(cardPayment = null) {
        if(!cardPayment) return this.clearCreditCardPayment();
        Object.assign(this.data, { cardPayment });
    }

    clearCreditCardPayment() {
        const defaultPayment = this._getDefaultPayment(this.data.selectedPayment.method);
        Object.assign(this.data.selectedPayment, defaultPayment);
        Object.assign(this.data.tempPayment, defaultPayment);
    }

    clearTempCreditCardPayment() {
        const defaultPayment = this._getDefaultPayment(this.data.selectedPayment.method);
        Object.assign(this.data.tempPayment, defaultPayment);
    }

    setTempCreditCardPayment(creditCard) {
        const method = 'CREDIT';
        Object.assign(this.data.tempPayment, creditCard, { method });
    }

    setCreditCardPayment(creditCard) {
        const method = 'CREDIT';
        Object.assign(this.data.tempPayment, creditCard, { method });
        Object.assign(this.data.selectedPayment, creditCard, { method });
    }

    setPaymentMethod(method) {
        Object.assign(this.data.selectedPayment, { method });
        Object.assign(this.data.tempPayment, { method });
    }

    clearTip() {
        this.data.selectedPayment.tip = 0;
        this.data.tempPayment.tip = 0;
    }

    getFormattedCreditCard(creditCard) {
        if(creditCard.guid) {
            return creditCard;
        }
        return {
            cardBrand: '',
            isPrimary: creditCard.isPrimary,
            maskedPan: `XXXX-${creditCard.cardNumber.substr(-4)}`,
            expirationMonth: this.getExpirationMonth(creditCard.expirationDate),
            expirationYear: this.getExpirationYear(creditCard.expirationDate)
        };
    }

    isPaymentTippable(payment = this.data.selectedPayment) {
        return payment.method === 'CREDIT';
    }

    _hasTipAndCheckDataForDisplay() {
        return _.hasIn(this.data, 'selectedPayment')
            && _.isObject(this.data.selectedPayment)
            && _.hasIn(this.cartService, 'data.cart.check')
            && _.isObject(this.cartService.data.cart.check);
    }

    /**
     * Returns the tip that should be used for display purposes. Will use the client-side tip
     * in preference to what was returned from the server in order to reflect any local changes.
     *
     * @returns {number}
     */
    getTipAmountForDisplay() {
        if (!this._hasTipAndCheckDataForDisplay()) {
            return 0;
        }

        const clientTip = this.isPaymentTippable() ? this.data.selectedPayment.tip : 0;
        const serverTip = this.cartService.data.cart.check.tip;

        return clientTip || serverTip;
    }

    /**
     * Returns the total check amount that should be used for display purposes. Will use the client-side tip
     * in preference to what was returned from the server in order to reflect any local changes.
     *
     * @returns {number}
     */
    getTotalAmountForDisplay() {
        if (!this._hasTipAndCheckDataForDisplay()) {
            return 0;
        }

        if (this.isPaymentTippable() && this.data.selectedPayment.tip) {
            // Remove whatever tip is currently stored on the server from the total and use the client-side tip instead.
            // So long as the tip isn't taxed, this should be fine to calculate client-side.
            return this.cartService.data.cart.check.totalAmount - this.cartService.data.cart.check.tip + this.data.selectedPayment.tip;
        } else {
            return this.cartService.data.cart.check.totalAmount;
        }
    }

    getExpirationMonth(expirationString) {
        if(!expirationString) return null;
        if(typeof expirationString !== 'string') return null;
        if(expirationString.length !== 4) return null;
        return expirationString.substring(0, 2);
    }

    getExpirationYear(expirationString) {
        if(!expirationString) return null;
        if(typeof expirationString !== 'string') return null;
        if(expirationString.length !== 4) return null;
        return expirationString.substring(expirationString.length, expirationString.length - 2);
    }

    expirationDateValid(expirationString) {
        const month = this.getExpirationMonth(expirationString);
        const year = this.getExpirationYear(expirationString);
        const currentDate = this.restaurantHoursService.data.initialMoment;
        const currentYear = currentDate.format('YY');
        const currentMonth = currentDate.format('MM');
        const maxMonth = 12;

        if(!month || !year) return false;
        if(month > maxMonth) return false;
        if(year === currentYear) return month >= currentMonth;
        return year >= currentYear;
    }

    validate(creditCard = this.data.selectedPayment) {
        if(this.validateFast(creditCard)) return this.$q.resolve();
        return this.$q.reject('Credit Card Invalid');
    }

    validateFast(creditCard = this.data.selectedPayment) {
        if(creditCard.method !== 'CREDIT') return true;
        const NewCardComplete = (creditCard.cardNumber && creditCard.expirationDate && creditCard.securityCode && creditCard.postalCode);
        if(creditCard.guid || NewCardComplete) return true;
        return false;
    }

    initialize() {
        let hoursDfd = this.restaurantHoursService.initialize();
        let cartDfd = this.cartService.getCart();
        let restaurantDfd = this.restaurantInfoFactory.getInfo(['onlineOrder']);

        return this.$q
            .all([cartDfd, restaurantDfd, hoursDfd])
            .then(res => this._setPaymentMethods(
                res[0],
                res[1].onlineOrder.paymentOptions
            ));
    }

    _setPaymentMethods(cart, allPaymentOptions) {
        let diningOption = cart.diningOptionBehavior;
        let fulfillmentDateTime = cart.fulfillmentDateTime;
        const validators = this._getValidators(diningOption, allPaymentOptions);
        this._setCashMethod(diningOption, validators);
        this._setCardMethod(fulfillmentDateTime, validators);
        if(!this.data.cashPayment) {
            this.setPaymentMethod('CREDIT');
        }
        if(!this.data.cardPayment) {
            this.setPaymentMethod('CASH');
        }
    }

    _setCashMethod(diningOption, validators) {
        if(diningOption === this.ORDER_TYPES.DELIVERY) {
            if(validators.allowCash) {
                this.setCashPayment(this.allMethods.CASH_DELIVERY);
                return;
            }
        }
        else if(validators.allowCash) {
            if(validators.allowCCInStore) {
                this.setCashPayment(this.allMethods.IN_STORE);
                return;
            }
            this.setCashPayment(this.allMethods.CASH_TAKEOUT);
            return;
        }
        else if(validators.allowCCInStore) {
            this.setCashPayment(this.allMethods.CREDIT_IN_STORE);
            return;
        }
        this.setCashPayment();
    }

    _setCardMethod(fulfillmentDateTime, validators) {
        let isSameDay = this.restaurantHoursService.isSameDay(fulfillmentDateTime);
        if(isSameDay && validators.allowCCSameDay || !isSameDay && validators.allowCCFuture) {
            this.setCardPayment(this.allMethods.CREDIT);
            return;
        }
        this.setCardPayment();
    }

    _getValidators(diningOption, allPaymentOptions) {
        if(diningOption === this.ORDER_TYPES.DELIVERY) {
            return allPaymentOptions.delivery;
        }
        return allPaymentOptions.takeout;
    }

    _getPaymentMethod() {
        const localStoragePayment = this.localStorageService.get(this.LOCAL_STORAGE.PAYMENT);
        return (localStoragePayment && localStoragePayment.method) ? localStoragePayment.method : 'CREDIT';
    }

    rollback() {
        Object.assign(this.data.tempPayment, this.data.selectedPayment);
    }
}
